import {
  Card,
  Stack,
  Dialog,
  Tooltip,
  Collapse,
  IconButton,
  Typography,
  CardContent,
  DialogTitle,
  DialogContent,
  useMediaQuery
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { Close, Fullscreen } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { ContactTableNestedRowTabs } from "./ContactTableNestedRowTabs";
import { preloadContactDetail } from "../../detail/data/sessionCache";
import {
  useOpenContactDetail,
  preloadContactDetailViewChunks
} from "../../detail/ContactDetailView";
import type {
  RealEstateContact,
  FavoriteListingsOfContact
} from "../../../../../@types/sharedSchema";
import type { RealEstateTab } from "../core/ContactTableTabs";
import type { NestedRowTabType } from "./ContactTableNestedRowTabs";

type ViewContactDialogOptions = {
  currentTab: null | RealEstateTab;
  handleClose: () => void;
  initialNestedTab?: NestedRowTabType;
};

type ViewContactDialogProps = {
  open: boolean;
  contact: RealEstateContact;
  favoriteListingsOfContact: FavoriteListingsOfContact;
  contactTableAdditionalProps?: ViewContactDialogOptions;
  handleEditContactDialogOpen: () => void;
  /** Optional stacking override. Callers that open this dialog from ON TOP of
   *  a non-MUI overlay (the signatures detail side-panel sits at zIndex 2001)
   *  must raise it above that overlay — MUI modals default to 1300, so it
   *  would otherwise open underneath. */
  zIndex?: number;
  /** Narrow-container layout for the inline (Collapse) mode — see the prop of the
   *  same name on `ContactTableNestedRowTabs`. Default off; forwarded only, this
   *  component does not read it. Used by the CRM side panel, which is ~420px wide
   *  on a desktop viewport that the tab strip's media query would call "wide". */
  compact?: boolean;
};

export function ViewContactDialog({
  open,
  contact,
  favoriteListingsOfContact,
  contactTableAdditionalProps,
  handleEditContactDialogOpen,
  zIndex,
  compact
}: ViewContactDialogProps): JSX.Element {
  const { t: translate } = useTranslation();
  const openContactDetail = useOpenContactDetail();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isInContactPage = contactTableAdditionalProps;

  const { currentTab, handleClose, initialNestedTab } = contactTableAdditionalProps ?? {};

  const tabsContent = (
    <ContactTableNestedRowTabs
      contact={contact}
      currentTab={currentTab}
      favoriteListingsOfContact={favoriteListingsOfContact}
      handleEditContactDialogOpen={handleEditContactDialogOpen}
      initialNestedTab={initialNestedTab}
      compact={compact}
    />
  );

  if (isInContactPage) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        fullScreen={isMobile}
        maxWidth="lg"
        PaperProps={{ sx: { height: "100%" } }}
        sx={zIndex ? { zIndex } : undefined}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{translate("contactsPage.contactDetails.title")}</Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Tooltip title="מסך מלא">
                <IconButton
                  onMouseEnter={() => {
                    preloadContactDetail(contact);
                    preloadContactDetailViewChunks();
                  }}
                  onClick={() => {
                    // Jump from the dialog to the full customer page (עמוד
                    // לקוח) — opened IN-PLACE over the contacts list when
                    // we're on it, via the standalone route from any other
                    // page (calendar, calls, chat, ...).
                    handleClose?.();
                    openContactDetail(contact.id);
                  }}
                >
                  <Fullscreen />
                </IconButton>
              </Tooltip>
              <IconButton onClick={handleClose}>
                <Close />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 1.5, md: 3 } }}>{tabsContent}</DialogContent>
      </Dialog>
    );
  }

  // Inline (chrome-less) mode. `compact` additionally drops the outlined Card: its
  // caller — the CRM side panel — is already a surface with its own border and header,
  // so the Card would render as a card inside a card.
  return (
    <Collapse sx={{ width: "100%" }} in={open} timeout="auto" unmountOnExit>
      {compact ? (
        tabsContent
      ) : (
        <Card variant="outlined">
          <CardContent>{tabsContent}</CardContent>
        </Card>
      )}
    </Collapse>
  );
}
